import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { CardTitle } from '@/components/ui/card';
import { ServiceCard } from '@/components/page-components/project/agents/business/ServiceCard';
import { ServiceForm } from '@/components/page-components/project/agents/business/ServiceForm';
import type { Service } from '@/types/Service';

export interface SetupServiceItem {
  id: string;
  name: string;
  description: string;
  durationMinutes: number;
  price: number | null;
  enabled: boolean;
}

interface ServicesSetupStepProps {
  services: SetupServiceItem[];
  onChange: (services: SetupServiceItem[]) => void;
}

const ServicesSetupStep: React.FC<ServicesSetupStepProps> = ({
  services,
  onChange,
}) => {
  const { t } = useTranslation();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingServiceId, setEditingServiceId] = useState<string | null>(null);

  const selectedService = useMemo(
    () => services.find(service => service.id === editingServiceId) ?? null,
    [services, editingServiceId]
  );

  const mappedServices = useMemo<Service[]>(
    () =>
      services.map((service, index) => ({
        id: index + 1,
        agent_id: 0,
        name: service.name,
        description: service.description || null,
        duration_minutes: service.durationMinutes,
        price: service.price,
        enabled: service.enabled,
        created_at: new Date().toISOString(),
      })),
    [services]
  );

  const selectedServiceForForm = useMemo(() => {
    if (!selectedService) {
      return null;
    }

    return {
      id: 1,
      agent_id: 0,
      name: selectedService.name,
      description: selectedService.description || null,
      duration_minutes: selectedService.durationMinutes,
      price: selectedService.price,
      enabled: selectedService.enabled,
      created_at: new Date().toISOString(),
    } satisfies Service;
  }, [selectedService]);

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <CardTitle>
            {t('agent.new.guided.services.title.translation', 'Services')}
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            {t(
              'agent.new.guided.services.description.translation',
              'Add your most common services so the agent can answer pricing and duration questions.'
            )}
          </p>
        </div>
        <Button
          type="button"
          variant="brand"
          onClick={() => {
            setEditingServiceId(null);
            setIsFormOpen(true);
          }}
        >
          <Plus className="mr-2 h-4 w-4" />
          {t('agent.new.guided.services.add.translation', 'Add service')}
        </Button>
      </div>

      {mappedServices.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border bg-card p-6 text-sm text-muted-foreground">
          {t(
            'agent.new.guided.services.empty.translation',
            'You can skip this step and add services later in Business settings.'
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {mappedServices.map((service, index) => (
            <ServiceCard
              key={services[index].id}
              service={service}
              onEdit={() => {
                setEditingServiceId(services[index].id);
                setIsFormOpen(true);
              }}
              onDelete={() => {
                onChange(
                  services.filter(
                    existingService => existingService.id !== services[index].id
                  )
                );
              }}
              onToggleEnabled={async (_serviceId, enabled) => {
                onChange(
                  services.map(existingService =>
                    existingService.id === services[index].id
                      ? { ...existingService, enabled }
                      : existingService
                  )
                );
              }}
            />
          ))}
        </div>
      )}

      <ServiceForm
        isOpen={isFormOpen}
        onClose={() => {
          setIsFormOpen(false);
          setEditingServiceId(null);
        }}
        service={selectedServiceForForm}
        agentId={0}
        onSave={async serviceData => {
          const nextService: SetupServiceItem = {
            id: selectedService?.id ?? crypto.randomUUID(),
            name: serviceData.name?.trim() ?? '',
            description: serviceData.description?.trim() ?? '',
            durationMinutes: serviceData.duration_minutes ?? 60,
            price: serviceData.price ?? null,
            enabled: serviceData.enabled ?? true,
          };

          if (selectedService) {
            onChange(
              services.map(existingService =>
                existingService.id === selectedService.id
                  ? nextService
                  : existingService
              )
            );
          } else {
            onChange([...services, nextService]);
          }
        }}
      />
    </div>
  );
};

export default ServicesSetupStep;
